import Text from "common/Typography/Text";
import {
  PLATFORM_TO_DISPLAY_NAME,
  PLATFORM_TO_KEY,
} from "@shared/features/settings/constants";
import Flex from "common/Boxes/Flex";
import Switch from "common/Buttons/Switch";
import styled, { css, useTheme } from "styled-components";
import { Globe, Send } from "lucide-react";
import useMedia from "utils/hooks/useMedia";
import { LARGE_MOBILE } from "common/constants";
import {
  PlatformsSwitchProps,
  TelegramCouplingType,
} from "@shared/features/settings/types";
import { Telegram } from "common/Icons/Icons";
import GlobalOutlined from "@ant-design/icons/GlobalOutlined";
import Col from "antd/es/col";
import Row from "antd/es/row";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import { translateOptionName } from "@shared/common/i18n/optionNames";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import SettingsRowLabel from "features/settings/NewLook/SettingsRowLabel.tsx";

const PlatformsSwitch = ({
  notificationsData,
  onSwitchClick,
  columnSpans,
}: PlatformsSwitchProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  const isMobile = useMedia([`(max-width: ${LARGE_MOBILE})`], [true], false);
  const { isNewLook } = useTheme();

  const platformToIcon = isNewLook
    ? {
        [TelegramCouplingType.TELEGRAM_BOT]: (
          <Send size={15} strokeWidth={1.75} />
        ),
        [TelegramCouplingType.WEB_OR_APP]: (
          <Globe size={15} strokeWidth={1.75} />
        ),
      }
    : {
        [TelegramCouplingType.TELEGRAM_BOT]: <TelegramIcon />,
        [TelegramCouplingType.WEB_OR_APP]: <WebsiteIcon />,
      };

  if (isNewLook) {
    return (
      <>
        {Object.keys(PLATFORM_TO_DISPLAY_NAME).map((platform) => {
          const isChecked = notificationsData?.[PLATFORM_TO_KEY[platform]];
          const disabled = isChecked === null || isChecked === undefined;
          return (
            <ChannelRow key={platform}>
              <SettingsRowLabel icon={platformToIcon[platform]}>
                {PLATFORM_TO_DISPLAY_NAME[platform]}
              </SettingsRowLabel>
              <StyledSwitch
                value={isChecked}
                onChange={() =>
                  onSwitchClick(Number(platform), !isChecked, disabled)
                }
                aria-label={PLATFORM_TO_DISPLAY_NAME[platform]}
                $disabled={disabled}
              />
            </ChannelRow>
          );
        })}
      </>
    );
  }

  return (
    <PlatformsRow>
      <Col span={columnSpans?.[0]}>
        <Text>
          {isMobile
            ? Object.values(PLATFORM_TO_DISPLAY_NAME)
                .map((platformName) => translateOptionName(platformName))
                .join("/")
            : t("notifications.receive_notifications")}
        </Text>
      </Col>
      {Object.keys(PLATFORM_TO_DISPLAY_NAME).map((platform, index) => {
        const isChecked = notificationsData?.[PLATFORM_TO_KEY[platform]];
        const disabled = isChecked === null || isChecked === undefined;
        return (
          <Col key={index} span={columnSpans?.[index + 1]}>
            <Flex $justifyContent={isNewLook ? "center" : "flex-end"}>
              {platformToIcon[platform]}
              {!isMobile && (
                <PlatformName>
                  {translateOptionName(PLATFORM_TO_DISPLAY_NAME[platform])}
                </PlatformName>
              )}
              <StyledSwitch
                value={isChecked}
                onChange={() =>
                  onSwitchClick(Number(platform), !isChecked, disabled)
                }
                size="small"
                $disabled={disabled}
              />
            </Flex>
          </Col>
        );
      })}
    </PlatformsRow>
  );
};

export default PlatformsSwitch;

const PlatformsRow = styled(Row)`
  margin-bottom: 24px;
  flex-wrap: wrap;
  width: 100%;
  line-height: 30px;
  align-items: center;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-bottom: 8px;
      padding: 8px 12px;
      border-radius: 12px;
      background: ${p.theme.colors.cardLightBgColor};
      color: ${p.theme.colors.secondaryText};
      font-size: 13px;
      svg {
        flex: none;
        color: ${p.theme.colors.primaryText};
      }
      .ant-typography {
        font-size: 13px;
      }
    `}
`;
const PlatformName = styled(Text)`
  margin-left: 6px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        color: ${p.theme.colors.primaryText};
        font-weight: ${p.theme.typography.fontWeights.MEDIUM};
      }
    `}
`;
const TelegramIcon = styled(Telegram)`
  fill: ${(p) => p.theme.colors.primaryText};
  font-size: 16px;
`;
const WebsiteIcon = styled(GlobalOutlined)`
  color: ${(p) => p.theme.colors.primaryText};
`;
const StyledSwitch = styled(Switch)<{ $disabled?: boolean }>`
  margin-left: 8px;
  ${(p) =>
    p.$disabled &&
    `
      opacity: 0.65;
      cursor: not-allowed;
   `};
`;
const ChannelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 60px;
  padding: 10px 20px;
  &:not(:last-child) {
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
